// What units is this garment in? — the pure half.
//
// Marvelous Designer writes centimetres by default and states the unit
// NOWHERE in the glTF it exports: there is no `asset.extras` scale, no unit
// hint, nothing. Measured on Sara's isaac_tshirtJoggers exports (ENG-1108),
// the file spans 12.238..158.879 in its own units, and at x0.01 the joggers
// land on Aoi's jeans to within 1 mm over a 0.9 m span — so it is centimetres,
// and the app's world is metres.
//
// A hardcoded x0.01 would then be right for every file we have and wrong for
// the first one exported in metres, silently, by a factor of a hundred: the
// garment would appear as a speck at the character's feet, which reads as "the
// importer is broken" rather than "the unit guess was wrong". So the scale is
// INFERRED from the one thing the file does tell us — how big it is — and the
// inference always says what it concluded and what else it considered.
//
// No three.js import here, so node:test drives the maths.

/** Metres per unit, for the units a mesh export realistically uses. */
export const UNIT_SCALES = Object.freeze({ m: 1, cm: 0.01, mm: 0.001 })

/**
 * The vertical extent, in metres, of a typical full garment (collar to ankle
 * cuff). Sara's t-shirt + joggers measure 1.466 m. This is the target the
 * candidate readings are scored against — not a limit, just the centre.
 */
export const TARGET_EXTENT_M = 1.2

/**
 * Outside this, no reading is a garment on a human. A 0.1 m span is a doll's
 * sleeve and a 3 m span is a marquee; either means the file is not what we
 * think, and guessing a scale for it would hide that.
 */
export const PLAUSIBLE_M = Object.freeze({ min: 0.1, max: 3.0 })

/**
 * Infer the unit of a garment from its vertical extent.
 *
 * Scores each candidate unit by how close the resulting real-world size lands
 * to `TARGET_EXTENT_M`, in LOG space — so "ten times too big" and "ten times
 * too small" are penalised equally, which a linear distance does not do. The
 * winner must still land inside `PLAUSIBLE_M`; when none does, the result is
 * `unknown` at scale 1 and the caller renders the garment untouched, wrong but
 * visibly wrong.
 *
 * @param {number} extent  the garment's Y extent in the file's own units
 * @param {{ unit?: keyof typeof UNIT_SCALES }} [opts]  an explicit unit always wins
 * @returns {{ unit: string, scale: number, extentM: number, reason: string,
 *             alternatives: Array<{ unit: string, extentM: number }> }}
 */
export function inferUnitScale(extent, opts = {}) {
  if (opts.unit != null) {
    const scale = UNIT_SCALES[opts.unit]
    if (scale == null) {
      throw new Error(`[garment] unknown unit "${opts.unit}" — expected one of ${Object.keys(UNIT_SCALES).join(', ')}`)
    }
    return {
      unit: opts.unit,
      scale,
      extentM: extent * scale,
      reason: `explicit unit "${opts.unit}" (x${scale}); inference not consulted`,
      alternatives: [],
    }
  }

  const finite = Number.isFinite(extent) && extent > 0
  const scored = finite
    ? Object.entries(UNIT_SCALES)
        .map(([unit, scale]) => ({ unit, scale, extentM: extent * scale }))
        .filter((c) => c.extentM >= PLAUSIBLE_M.min && c.extentM <= PLAUSIBLE_M.max)
        .sort((a, b) => Math.abs(Math.log(a.extentM / TARGET_EXTENT_M)) - Math.abs(Math.log(b.extentM / TARGET_EXTENT_M)))
    : []

  if (scored.length === 0) {
    return {
      unit: 'unknown',
      scale: 1,
      extentM: finite ? extent : 0,
      reason: `could not infer a unit: an extent of ${extent} is not a garment in ${Object.keys(UNIT_SCALES).join(', ')} `
        + `(nothing lands in ${PLAUSIBLE_M.min}..${PLAUSIBLE_M.max} m). Rendering unscaled.`,
      alternatives: [],
    }
  }

  const [best, ...rest] = scored
  const alt = rest.length ? `; also plausible: ${rest.map((c) => `${c.unit} -> ${c.extentM.toFixed(3)} m`).join(', ')}` : ''
  return {
    unit: best.unit,
    scale: best.scale,
    extentM: best.extentM,
    reason: `extent ${extent.toFixed(3)} reads as ${best.unit} -> ${best.extentM.toFixed(3)} m tall${alt}`,
    alternatives: rest.map(({ unit, extentM }) => ({ unit, extentM })),
  }
}
